import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyAiEdit, previewChat, getUserStatus, UserStatus, TripContent, PatchResultSummary, AiEditContext } from '../../services/api';
import { ChatMessage } from '../ChatMessages';
import ChatTextBox from './ChatTextBox.tsx';

// Format summary into readable message (fallback for content string)
function formatSummaryMessage(summary: PatchResultSummary): string {
    const bullets = summary.bullets.join(' ');
    const suggestion = summary.nextSuggestion ? ` ${summary.nextSuggestion}` : '';
    return bullets + suggestion;
}

interface ChatComposerProps {
    tripSlug: string;
    userStatus: UserStatus | null;
    isTemplate: boolean;
    onShowUnlockModal: () => void;
    onTripUpdated: (content: TripContent, versionNo: number) => void;
    onUserStatusUpdate: (status: UserStatus) => void;
    onClone: () => void;
    isSubmitting: boolean;
    setIsSubmitting: (sub: boolean) => void;
    addMessage: (msg: ChatMessage) => void;
    editContext?: AiEditContext;
}

/**
 * ChatComposer - Handles the business logic for AI interactions.
 * Switches between /api/ai/preview (for templates) and /api/ai/edit (for working copies).
 */
export default function ChatComposer({
    tripSlug,
    userStatus,
    isTemplate,
    onShowUnlockModal,
    onTripUpdated,
    onUserStatusUpdate,
    onClone,
    isSubmitting,
    setIsSubmitting,
    addMessage,
    editContext,
}: ChatComposerProps) {
    const { t, i18n } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState<string | null>(null);


    const handleSubmit = async () => {
        if (!inputValue.trim() || isSubmitting) return;

        // AUTH & ENTITLEMENT BLOCCK
        if (!isTemplate) {
            if (!userStatus?.isAuthenticated) {
                onShowUnlockModal(); // This opens Login/Signup for Guest
                return;
            }
            if (!userStatus.isOwner) {
                // Non-owners must clone first
                setError(t('chat.clone_to_edit'));
                return;
            }
            if (!userStatus.hasEditsAvailable) {
                onShowUnlockModal(); // This opens Upgrade Modal for Owner with 0 edits
                return;
            }
        }

        const userMsg: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'user',
            content: inputValue.trim(),
            timestamp: new Date(),
        };

        addMessage(userMsg);
        setInputValue('');
        setIsSubmitting(true);
        setError(null);

        try {
            if (isTemplate) {
                // Preview mode: Text response only, no data modification
                const result = await previewChat(tripSlug, userMsg.content);
                addMessage({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: result.reply,
                    timestamp: new Date(),
                });
            } else {
                // Working copy mode: Apply patches and update trip content
                const result = await applyAiEdit(tripSlug, userMsg.content, editContext, i18n.language || 'en');
                if (result.success && result.content && result.versionNo) {
                    // Use humanized summary from backend
                    const summary = result.summary;
                    const messageContent = summary
                        ? formatSummaryMessage(summary)
                        : getSuccessMessage(t, result.patchApplied);

                    addMessage({
                        id: crypto.randomUUID(),
                        role: 'assistant',
                        content: messageContent,
                        timestamp: new Date(),
                        patchType: result.patchApplied,
                        summary: summary, // NEW: Pass summary for rich rendering
                    });
                    onTripUpdated(result.content, result.versionNo);

                    // Refresh status
                    const newStatus = await getUserStatus(tripSlug);
                    onUserStatusUpdate(newStatus);
                } else {
                    // Error case: show error in chat
                    addMessage({
                        id: crypto.randomUUID(),
                        role: 'assistant',
                        content: result.error || t('chat.failed_to_apply'),
                        timestamp: new Date(),
                    });
                }
            }
        } catch (err: any) {
            // Add error message to chat so user sees it in conversation
            const errorMsg = err.error || err.message || t('chat.connection_failed');
            addMessage({
                id: crypto.randomUUID(),
                role: 'assistant',
                content: `Sorry, ${errorMsg}`,
                timestamp: new Date(),
            });

            if (err.code === 'QUOTA_EXCEEDED') {
                onShowUnlockModal();
            } else if (err.code === 'ACCESS_DENIED') {
                setError(t('chat.clone_to_edit'));
            } else {
                setError(errorMsg);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="chat-composer-container">
            {isTemplate && (
                <div className="chat-preview-cta">
                    <button className="preview-customize-btn" onClick={onClone}>
                        {t('chat.customize_itinerary')}
                    </button>
                    <p>{t('chat.preview_mode_notice')}</p>
                </div>
            )}

            {!isTemplate && !userStatus?.isOwner && userStatus?.isAuthenticated && (
                <div className="chat-preview-cta">
                    <button className="preview-customize-btn" onClick={onClone}>
                        {t('chat.clone_to_edit_cta')}
                    </button>
                    <p>{t('chat.shared_trip_notice')}</p>
                </div>
            )}

            {!isTemplate && !userStatus?.isAuthenticated && (
                <div className="chat-preview-cta">
                    <button className="preview-customize-btn" onClick={onShowUnlockModal}>
                        {t('chat.sign_in_clone_cta')}
                    </button>
                    <p>{t('chat.private_trip_notice')}</p>
                </div>
            )}

            <ChatTextBox
                value={inputValue}
                onChange={setInputValue}
                onSubmit={handleSubmit}
                disabled={isSubmitting}
                placeholder={isTemplate ? t('chat.ask_about_trip') : t('chat.example_edit')}
                error={error}
                hint={getHintText(t, userStatus, isTemplate)}
            />
        </div>
    );
}

function getHintText(t: any, userStatus: UserStatus | null, isTemplate: boolean) {
    if (isTemplate) return t('chat.explore_itinerary');
    if (!userStatus?.isAuthenticated) return t('chat.sign_in_clone_cta').replace('✨ ', '');
    if (!userStatus.isOwner) return t('chat.clone_to_edit');

    // User owns this trip
    if (userStatus.isPremium) {
        return t('chat.unlimited_edits');
    }

    // Check if user has purchased access to this trip (or its source)
    if (userStatus.isTripUnlocked) {
        return t('chat.your_trip');
    }

    // Owner but hasn't paid - show unlock prompt
    return t('chat.unlock_to_edit');
}

function getSuccessMessage(t: any, patchType?: string): string {
    const messages: Record<string, string> = {
        add_activity: t('chat.done_added'),
        remove_activity: t('chat.done_removed'),
        update_activity: t('chat.done_updated'),
        move_activity: t('chat.done_moved'),
    };
    return messages[patchType || ''] || t('chat.done_generic');
}
